// src/views/Settings/AccountSettings.jsx
//
// El alcance de la cuenta: lo que cambia al cambiar de organización, porque es
// propiedad de la cuenta y no de la persona. El dinero vive aquí —en `accounts`
// es donde el backend guarda el tier y el saldo—, y las credenciales también.
//
// El id va en la URL a propósito. Con el alcance en estado, dos pestañas del
// navegador en cuentas distintas se pisan y un enlace compartido abre la cuenta
// equivocada; con el id en la ruta, ninguna de las dos cosas puede pasar.
import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccountGeneralSection from './account/AccountGeneralSection';
import BillingSection from './account/BillingSection';
import CredentialsSection from './account/CredentialsSection';
import ProjectsSection from './account/ProjectsSection';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  SCOPES,
  isSettingsSection,
  defaultSectionFor,
  settingsPath,
} from '../../lib/settingsNav';

export default function AccountSettings() {
  const { accountId, section } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const navigate = useNavigate();
  const { currentAccount, loadPersonalAccount } = useOrganization();

  const active = isSettingsSection(SCOPES.ACCOUNT, section) ? section : null;

  useEffect(() => {
    if (!active) {
      navigate(settingsPath(SCOPES.ACCOUNT, defaultSectionFor(SCOPES.ACCOUNT), { accountId }), {
        replace: true,
      });
    }
  }, [active, accountId, navigate]);

  // La vista lee la cuenta del contexto, no del parámetro. Si un enlace apunta a
  // otra cuenta —o si cambias de organización con esto abierto—, la URL diría una
  // cosa y la pantalla mostraría otra. Se canonicaliza en vez de mentir: cambiar
  // de contexto desde aquí no es algo que la URL pueda hacer sola, porque el
  // backend expone la cuenta a través de la organización, no al revés.
  const sessionAccountId = currentAccount?.id;
  useEffect(() => {
    if (!sessionAccountId || !accountId || sessionAccountId === accountId) return;
    navigate(
      { pathname: settingsPath(SCOPES.ACCOUNT, section || 'billing', { accountId: sessionAccountId }), search },
      { replace: true }
    );
  }, [sessionAccountId, accountId, section, search, navigate]);

  if (!active) return null;

  // La pestaña de facturación vive en `?tab=`. `coins` además tiene que ser una
  // URL real y estable: es a donde Tilopay redirige de vuelta con `?order=`.
  const billingTab = searchParams.get('tab') || 'summary';
  const setBillingTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'summary') params.delete('tab');
    else params.set('tab', next);
    // Salir de coins deja atrás el `?order=` de una compra ya confirmada; si no,
    // volver a la pestaña la mostraría confirmando un pago viejo.
    if (next !== 'coins') params.delete('order');
    setSearchParams(params);
  };

  // El shell lo monta `SettingsLayout`; aquí solo el contenido.
  return (
    <>
      {active === 'general' && (
        <AccountGeneralSection
          account={currentAccount}
          onRefreshAccount={loadPersonalAccount}
        />
      )}
      {active === 'billing' && (
        <BillingSection
          tab={billingTab}
          onTabChange={setBillingTab}
          onViewPlans={() => setBillingTab('plan')}
        />
      )}
      {active === 'credentials' && <CredentialsSection />}
      {active === 'projects' && <ProjectsSection />}
    </>
  );
}
